import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { DateTime } from 'luxon';

import { Reservation } from 'src/models/schedule-weekly-view.model';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { ReservationPopoverComponent } from '../reservation-popover/reservation-popover.component';
import { ReservationInteractionService } from '../../services/reservation-interaction.service';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

export type ReservationCardSize = 'sm' | 'md' | 'lg';

type ReservationStatus =
  | 'inquiry'
  | 'quote'
  | 'hold'
  | 'cancelled'
  | 'no_show'
  | 'check_in_today'
  | 'check_out_today'
  | 'in_stay'
  | 'upcoming'
  | 'past';

@Component({
  selector: 'app-reservation-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './reservation-card.component.html',
  styleUrls: ['./reservation-card.component.scss'],
  imports: [
    CommonModule,
    IonicModule,
    MatMenuModule,
    TidyUiComponentsModule,
    ReservationPopoverComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ReservationCardComponent implements OnDestroy {
  private readonly interaction = inject(ReservationInteractionService);
  private readonly windowService = inject(WindowService);
  private readonly util = inject(Util);

  @Input() reservation!: Reservation;
  @Input() size: ReservationCardSize = 'sm';
  @Input() address?: string;
  @Input() addressId?: number | string;
  @Input() usePopover = true;

  @Output() readonly cardClick = new EventEmitter<Reservation>();

  @ViewChild(MatMenuTrigger) trigger?: MatMenuTrigger;

  logoFailed = false;
  profilePictureFailed = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly HOVER_OPEN_DELAY = 120;
  private readonly HOVER_CLOSE_DELAY = 180;

  ngOnDestroy(): void {
    this.clearTimers();
  }

  get isDesktop(): boolean {
    return this.windowService.isDesktopRes;
  }

  private clearTimers(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  onCardMouseEnter(): void {
    if (!this.usePopover || !this.isDesktop) {return;}
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (this.trigger?.menuOpen) {return;}
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.trigger?.openMenu();
    }, this.HOVER_OPEN_DELAY);
  }

  onCardMouseLeave(): void {
    if (!this.usePopover || !this.isDesktop) {return;}
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.scheduleClose();
  }

  private scheduleClose(): void {
    if (this.closeTimer) {return;}
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.trigger?.closeMenu();
    }, this.HOVER_CLOSE_DELAY);
  }

  onPopoverHoverChange(hovered: boolean): void {
    if (!this.isDesktop) {return;}
    if (hovered) {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    } else {
      this.scheduleClose();
    }
  }

  get primaryLabel(): string {
    return this.reservation?.guestName?.trim() || this.reservation?.title || '';
  }

  get initials(): string {
    const name = this.primaryLabel.trim();
    if (!name) {
      return '?';
    }
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  }

  get avatarGradient(): string {
    return this.util.getAvatarGradient(this.initials || 'A');
  }

  get status(): ReservationStatus {
    const state = this.reservation?.state;
    if (state === 'inquiry') {return 'inquiry';}
    if (state === 'quote') {return 'quote';}
    if (state === 'hold') {return 'hold';}
    if (state === 'cancelled') {return 'cancelled';}
    if (state === 'no_show') {return 'no_show';}

    const today = DateTime.now().startOf('day');
    const realStartDate = this.reservation?.period?.startDate ?? this.reservation?.startDate;
    const realEndDate = this.reservation?.period?.endDate ?? this.reservation?.endDate;
    const start = realStartDate
      ? DateTime.fromISO(realStartDate).startOf('day')
      : null;
    const end = realEndDate
      ? DateTime.fromISO(realEndDate).startOf('day')
      : null;
    if (start && start.hasSame(today, 'day')) {
      return 'check_in_today';
    }
    if (end && end.hasSame(today, 'day')) {
      return 'check_out_today';
    }
    if (start && end && start < today && end > today) {
      return 'in_stay';
    }
    if (end && end < today) {
      return 'past';
    }
    return 'upcoming';
  }

  get statusLabel(): string {
    switch (this.status) {
      case 'inquiry':
        return 'Inquiry';
      case 'quote':
        return 'Quote';
      case 'hold':
        return 'Hold';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No show';
      case 'check_in_today':
        return 'Checking in';
      case 'check_out_today':
        return 'Checking out';
      case 'in_stay':
        return 'In progress';
      case 'past':
        return 'Past';
      case 'upcoming':
      default:
        return 'Upcoming';
    }
  }

  get showStatusPill(): boolean {
    const s = this.status;
    return s !== 'upcoming' && s !== 'past';
  }

  get statusIcon(): string {
    if (this.reservation?.stateIcon) {
      return this.reservation.stateIcon;
    }
    switch (this.status) {
      case 'inquiry':
        return '/assets/svg/chatbubble-outline.svg';
      case 'quote':
        return '/assets/svg/pricetag-outline.svg';
      case 'hold':
        return '/assets/svg/time-outline.svg';
      case 'cancelled':
      case 'no_show':
        return '/assets/svg/close-outline.svg';
      case 'check_in_today':
        return '/assets/svg/log-in-outline.svg';
      case 'check_out_today':
        return '/assets/svg/log-out-outline.svg';
      case 'in_stay':
        return '/assets/svg/home-outline.svg';
      case 'past':
        return '/assets/svg/checkmark-outline.svg';
      case 'upcoming':
      default:
        return '/assets/svg/calendar-outline.svg';
    }
  }

  get isInquiry(): boolean {
    return this.status === 'inquiry';
  }

  get isHold(): boolean {
    return this.status === 'hold';
  }

  get dateRange(): string {
    return this.reservation?.title || '';
  }

  get nightsLabel(): string {
    const n = this.reservation?.nights;
    return n ? `${n}n` : '';
  }

  get hasDues(): boolean {
    return Boolean(this.reservation?.owes && this.reservation.owes > 0);
  }

  get hasGuidebook(): boolean {
    return Boolean(this.reservation?.guidebookUrl) || Boolean(this.reservation?.id);
  }

  get sourceLogoUrl(): string | undefined {
    if (this.logoFailed) {
      return undefined;
    }
    return this.reservation?.sourceLogoUrl;
  }

  onLogoError(): void {
    this.logoFailed = true;
  }

  get guestProfilePicture(): string | undefined {
    if (this.profilePictureFailed) {
      return undefined;
    }
    return this.reservation?.guestProfilePicture || undefined;
  }

  onProfilePictureError(): void {
    this.profilePictureFailed = true;
  }

  get guestCounts(): { icon: string; count: number; label: string }[] {
    const r = this.reservation;
    const out: { icon: string; count: number; label: string }[] = [];
    const adults = r?.totalAdults ?? 0;
    const children = r?.totalChildren ?? 0;
    const infants = r?.totalInfants ?? 0;
    const pets = r?.totalPets ?? 0;
    if (adults) {
      out.push({ icon: 'assets/svg/person.svg', count: adults, label: 'adults' });
    }
    if (children) {
      out.push({ icon: 'assets/svg/people.svg', count: children, label: 'children' });
    }
    if (infants) {
      out.push({ icon: 'assets/svg/heart-outline.svg', count: infants, label: 'infants' });
    }
    if (pets) {
      out.push({ icon: 'assets/svg/paw.svg', count: pets, label: 'pets' });
    }
    if (!out.length && r?.totalGuests) {
      out.push({ icon: 'assets/svg/person.svg', count: r.totalGuests, label: 'guests' });
    }
    return out;
  }

  onCardClick(event: Event): void {
    event.stopPropagation();
    this.cardClick.emit(this.reservation);
    if (!this.usePopover) {
      this.interaction.openDetail(this.reservation, {
        address: this.address,
        addressId: this.addressId,
      });
      return;
    }
    this.trigger?.openMenu();
  }

  onGuidebookIcon(event: Event): void {
    event.stopPropagation();
    this.interaction.openGuidebook(this.reservation, {
      address: this.address,
      addressId: this.addressId,
    });
  }

  handleGuidebookFromPopover(): void {
    this.trigger?.closeMenu();
    this.interaction.openGuidebook(this.reservation, {
      address: this.address,
      addressId: this.addressId,
    });
  }

  handleDetailsFromPopover(): void {
    this.trigger?.closeMenu();
    this.interaction.openDetail(this.reservation, {
      address: this.address,
      addressId: this.addressId,
    });
  }

  handleMessageFromPopover(): void {
    this.trigger?.closeMenu();
    void this.interaction.openMessageThread(this.reservation);
  }
}
